import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateSystemConfigMutation } from '../../../../hooks/mutations/useSystemConfigMutation';
import { SystemConfig } from '../../../../services/apiSLTP/systemConfig';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    config: SystemConfig | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateSystemConfigModal: React.FC<ModalProps> = ({ config, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [description, setDescription] = useState(config?.description || '');
    const [value, setValue] = useState(config?.value);

    const { mutate: updateConfig } = useUpdateSystemConfigMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error updating config:', error.message),
    });

    const handleOk = () => {
        let parsedValue: any = value;

        if (typeof config?.value === 'number') {
            const asNumber = Number(value);
            parsedValue = isNaN(asNumber) ? value : asNumber;
        }
        const configData = { description, value: parsedValue };

        if (isEditMode && config?._id) {
            updateConfig({ id: config._id, data: configData });
        }
    };

    return (
        <ModalBody
            title={t('configuration')}
            subtitle={isEditMode ? t('update') : t('add')}
            description={config?.key}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!description.length || (typeof value === 'string' && !value.length)}
            onSubmit={handleOk}
        >
            {typeof config?.value === 'string' && (
                <CustomTextInput value={value} label={t('value')} onChange={setValue} type="text" required />
            )}
            {typeof config?.value === 'boolean' && (
                <ToggleLine checked={value} onChange={(checked) => setValue(checked)} title={capitalizeFirstLetter(t('value'))} />
            )}
            {typeof config?.value === 'number' && (
                <CustomTextInput value={value} label={t('value')} onChange={setValue} type="number" required />
            )}
            <CustomTextInput value={description} label={t('description')} onChange={setDescription} required />
        </ModalBody>
    );
};

export default UpdateSystemConfigModal;
